function normalizeHrSignalQualityValue(raw) {
  const value = typeof raw === "string" ? raw.trim().toLowerCase() : null;
  if (!value) return null;

  if (value === "very good" || value === "very_good" || value === "verygood") {
    return "very good";
  }

  if (
    value === "good" ||
    value === "pass" ||
    value === "passed" ||
    value === "ok"
  ) {
    return "good";
  }

  if (
    value === "bad" ||
    value === "fail" ||
    value === "failed" ||
    value === "low" ||
    value === "poor"
  ) {
    return "bad";
  }

  return null;
}

function mapHrSignalQualityScore(score) {
  if (typeof score !== "number" || !Number.isFinite(score)) return null;
  if (score >= 90) return "very good";
  if (score >= 80) return "good";
  return "bad";
}

function normalizeRespiratorySignalQualityValue(raw) {
  const value = typeof raw === "string" ? raw.trim().toLowerCase() : null;
  if (!value) return null;

  if (
    value === "pass" ||
    value === "passed" ||
    value === "ok" ||
    value === "good"
  ) {
    return "pass";
  }

  if (
    value === "fail" ||
    value === "failed" ||
    value === "bad" ||
    value === "poor" ||
    value.startsWith("fail")
  ) {
    return "fail";
  }

  return null;
}

function pickRespiratorySignalQualityFromChecks(checks) {
  if (!checks || typeof checks !== "object" || Array.isArray(checks)) {
    return null;
  }

  let sawPass = false;

  for (const value of Object.values(checks)) {
    const normalized = normalizeRespiratorySignalQualityValue(value);
    if (normalized === "fail") return "fail";
    if (normalized === "pass") sawPass = true;
  }

  return sawPass ? "pass" : null;
}

function pickFirstChecksObject(candidates) {
  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate)
    ) {
      return candidate;
    }
  }

  return null;
}

export function isPassingHrSignalQuality(quality) {
  return quality === "good" || quality === "very good";
}

export function pickHrSignalQuality(docData) {
  const d = docData || {};

  const categoricalCandidates = [
    d?.results?.heartdx?.data?.rppg_signal_quality,
    d?.results?.heartdx_signal_quality,
    d?.results?.heartdx?.rppg_signal_quality,
    d?.rppg_signal_quality,
    d?.ppg_signal_quality,
    d?.heartdx?.ppg_signal_quality,
    d?.signal_quality_text,
    d?.signalQualityText,
  ];

  for (const candidate of categoricalCandidates) {
    const normalized = normalizeHrSignalQualityValue(candidate);
    if (normalized) return normalized;
  }

  const scoreCandidates = [
    d?.results?.heartdx_signal_quality_score,
    d?.results?.heartdx?.data?.heartdx_signal_quality_score,
    d?.results?.heartdx?.data?.sqi,
  ];

  for (const candidate of scoreCandidates) {
    const normalized = mapHrSignalQualityScore(candidate);
    if (normalized) return normalized;
  }

  const legacyCode = d?.signal_quality_code ?? d?.signalQualityCode ?? null;
  if (typeof legacyCode === "number" && Number.isFinite(legacyCode)) {
    return legacyCode <= 1 ? "bad" : "good";
  }

  return null;
}

export function pickRr(docData) {
  const r = docData?.results || docData?.resp?.results || null;
  const rrRaw = r?.mpm?.rr_avg ?? null;

  return typeof rrRaw === "number" && Number.isFinite(rrRaw) ? rrRaw : null;
}

export function pickRrSignalQuality(docData) {
  const r = docData?.results || docData?.resp?.results || null;
  const respiratoryRate = r?.mpm?.respiratory_rate ?? null;
  const mpmSignalQuality = r?.mpm_signal_quality ?? r?.mpm?.mpm_signal_quality;

  const stringCandidates = [
    mpmSignalQuality?.status,
    respiratoryRate?.upstreamQuality?.status,
    r?.mpm?.quality,
    respiratoryRate?.qualityStatus,
    respiratoryRate?.quality,
    r?.rgb_signal_quality,
  ];

  for (const candidate of stringCandidates) {
    const normalized = normalizeRespiratorySignalQualityValue(candidate);
    if (normalized) return normalized;
  }

  const checksCandidates = [
    mpmSignalQuality?.checks,
    respiratoryRate?.upstreamQuality?.checks,
  ];

  for (const candidate of checksCandidates) {
    const normalized = pickRespiratorySignalQualityFromChecks(candidate);
    if (normalized) return normalized;
  }

  return null;
}

/**
 * Returns the individual respiratory quality checks when a processor supplies
 * them. The overall status remains the source of truth for whether a result
 * can be used; callers use these checks only to make repeat advice specific.
 */
export function pickRrSignalQualityChecks(docData) {
  const r = docData?.results || docData?.resp?.results || null;
  const respiratoryRate = r?.mpm?.respiratory_rate ?? null;
  const mpmSignalQuality = r?.mpm_signal_quality ?? r?.mpm?.mpm_signal_quality;

  return pickFirstChecksObject([
    mpmSignalQuality?.checks,
    respiratoryRate?.upstreamQuality?.checks,
    // Some older results persisted the per-check flags alongside metrics.
    // They are safe to use here because only recognised failed flags are read.
    mpmSignalQuality?.metrics,
    respiratoryRate?.upstreamQuality?.metrics,
  ]);
}
